/**
 * @fileoverview Componente Organismo para panel de grabación de audio en el Inbox.
 * Permite iniciar, cancelar y finalizar grabaciones de audio, mostrando el tiempo transcurrido y feedback visual.
 * Implementa arquitectura optimizada con separación de capas, integración de hooks y uso de TailwindCSS.
 *
 * @module RecordingAudioPanelMC
 * @category Component - Organism
 * @since 3.0.0
 * @version 3.1.0
 *
 * @typedef {Object} RecordingAudioPanelMCProps
 * @property {function} sendAudio - Callback para enviar el audio grabado
 * @property {function} [onRecordingStateChange] - Callback para notificar cambios de estado de grabación
 *
 * @component RecordingAudioPanelMC
 *
 * @description
 * **Características:**
 * - Permite grabar audio en el chat del Inbox
 * - Muestra tiempo transcurrido en formato mm:ss
 * - Botones accesibles para cancelar y finalizar grabación
 * - Feedback visual con colores semánticos y TailwindCSS
 * - Integración con hook useAudioRecorder para gestión de grabación
 * - Notifica cambios de estado mediante onRecordingStateChange
 *
 * **Atomic Design:**
 * - Nivel: Organism
 * - Composición: IconMC (atom), estructura flex (molecule)
 *
 * @param {RecordingAudioPanelMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Panel de grabación de audio
 *
 * @example
 * <RecordingAudioPanelMC sendAudio={handleSendAudio} onRecordingStateChange={handleStateChange} />
 *
 * @see {@link useAudioRecorder} Hook para gestión de grabación
 * @see {@link IconMC} Componente de ícono
 */
import React, { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAudioRecorder } from "../../../hooks/inbox/useAudioRecorder";
import { canRecordAudio } from "../../../../util/audioUtils";
import IconMC from "../../../components/IconMC";

const RecordingAudioPanelMC = ({ sendAudio, onRecordingStateChange }) => {
  const { t } = useTranslation('InboxMC');

  const {
    recording,
    minutes,
    secs,
    startRecording,
    stopRecording,
    cancelRecording
  } = useAudioRecorder({
    onFinish: sendAudio
  });

  useEffect(() => {
    onRecordingStateChange?.(recording);
  }, [recording, onRecordingStateChange]);

  // No MediaRecorder/getUserMedia (very old browsers): hide the mic — the agent
  // can still attach an audio file, which the backend normalizes.
  if (!canRecordAudio()) {
    return null;
  }

  if (!recording) {
    return (
      <IconMC
        name="mic-outline"
        size="xl"
        className="hover:tw-text-gray-600 tw-transition-colors tw-duration-150 tw-cursor-pointer"
        onClick={startRecording}
      />
    );
  }

  return (
    <div className="send-icon-tooltip" data-tooltip-content={t('chat.voice')}>
      <div className="tw-wrapper tw-inline-flex tw-items-center tw-gap-4 tw-m-0">
        <IconMC
          name="close-circle-outline"
          className="tw-text-red-500 tw-text-2xl"
          onClick={cancelRecording}
        />

        <div className="tw-flex tw-items-center tw-gap-1 tw-font-semibold tw-text-base">
          <span className="tw-text-lg">🔴</span>
          <span className="tw-text-lg">
            {` ${minutes}:${secs}`}
          </span>
        </div>

        <IconMC
          name="checkmark-circle-2-outline"
          className="tw-text-green-600 tw-text-2xl"
          onClick={stopRecording}
        />
      </div>
    </div>
  );
};

export default memo(RecordingAudioPanelMC);
